// Libraries
import React from 'react';
import update from 'react-addons-update';
import {css} from 'aphrodite/no-important';
// Core
import CoreMetrics, {FormFactorChangeListener, sharedFormFactorChangeListener, createExpandableMediaQueryStylesheet} from '../core/Metrics';
import {FixedSpace, FlexibleSpace} from '../core/Spacing';
// Screens
import GridSystem from './GridSystem';
import TypeSystem from './TypeSystem';
import Components from './Components';
import ContentTreeEditor from './ContentTreeEditor';
import HomePage from './HomePage';
import SubjectPage from './SubjectPage';
import UnitPage from './UnitPage';
import ArticlePage from './ArticlePage';
import VideoPage from './VideoPage';
import ScratchpadPage from './ScratchpadPage';
import MarketingPage from './MarketingPage';
import LoggedInHomePage from './LoggedInHomePage';
import ClassHomePage from './ClassHomePage';
import Modals from './Modals';
// Controls
import CheckBox from '../controls/CheckBox';
import {TempSelectBox} from '../controls/SelectBox';
// Layouts
import {Grid, GridRow, GridColumn, DockedTOCandContentLayout} from '../layouts/Grid';
import ToolBar from '../layouts/ToolBar';

// Constants
const Screen = {
    FixedWidthGridSystem: 0,
    FlexibleWidthGridSystem: 1,
    TypeSystem: 2,
    Components: 3,
    ContentTreeEditor: 4,
    HomePage: 5,
    SubjectPage: 6,
    UnitPage: 7,
    ArticlePage: 8,
    VideoPage: 9,
    ScratchpadPage: 10,
    MarketingPage: 11,
    LoggedInHomePage: 12,
    ClassHomePage: 13,
    Modals: 14,
};
const GridSystemVariant = {
    Centered: "centered",
    Stretched: "stretched",
    OffsetBySidebarAndCentered: "offsetBySidebarAndCentered",
    OffsetBySidebarAndStretched: "offsetBySidebarAndStretched",
};
const DefaultStartingScreen = "HomePage";
const ScreenQueryString = "?screen="

export default class Demo extends React.Component {
    constructor(props) {
        super(props);

        // The following code allows the user to navigate to a specific screen
        // via a url query parameter.
        // Example: localhost:3000?screen=UnitPage
        const url = window.location.href;
        const queryIndex = url.indexOf(ScreenQueryString);

        let startingScreenIndex = null;
        if (queryIndex !== -1) {
            startingScreenIndex = Screen[url.slice(
                queryIndex + ScreenQueryString.length)]
        } else {
            startingScreenIndex = Screen[DefaultStartingScreen];
            this.handleUpdateScreenQueryInUrl(DefaultStartingScreen);
        }

        this.state = {
            screen: startingScreenIndex,
            showGridColumns: true,
            showGridRows: false,
        };
    }
    componentWillMount() {
        window.addEventListener("keydown", this.handleKeyDown);
    }
    componentWillUnmount() {
        window.removeEventListener("keydown", this.handleKeyDown);
    }
    render() {
        const {
            screen,
            dockedSidebar,
            showGridColumns,
            showGridRows,
        } = this.state;

        const screens = Object.keys(Screen);
        let screenToRender = null;
        let gridSystemVariant = GridSystemVariant.Centered;
        let fixedHeight = false;

        if (screen === Screen.FixedWidthGridSystem) {
            screenToRender = <GridSystem />;
        } else if (screen === Screen.FlexibleWidthGridSystem) {
            screenToRender = <GridSystem
                variant={GridSystemVariant.Stretched}
            />;
            gridSystemVariant = GridSystemVariant.Stretched;
        } else if (screen === Screen.TypeSystem) {
            screenToRender = <TypeSystem />;
        } else if (screen === Screen.Components) {
            screenToRender = <Components />;
            gridSystemVariant = GridSystemVariant.Stretched;
        } else if (screen === Screen.ContentTreeEditor) {
            screenToRender = <ContentTreeEditor />;
            gridSystemVariant = GridSystemVariant.Stretched;
            fixedHeight = true;
        } else if (screen === Screen.HomePage) {
            screenToRender = <HomePage />;
        } else if (screen === Screen.SubjectPage) {
            screenToRender = <SubjectPage />;
        } else if (screen === Screen.UnitPage) {
            screenToRender = <UnitPage />;
            gridSystemVariant = GridSystemVariant.OffsetBySidebarAndCentered;
        } else if (screen === Screen.ArticlePage) {
            screenToRender = <ArticlePage />;
            gridSystemVariant = GridSystemVariant.OffsetBySidebarAndCentered;
        } else if (screen === Screen.VideoPage) {
            screenToRender = <VideoPage />;
            gridSystemVariant = GridSystemVariant.OffsetBySidebarAndCentered;
        } else if (screen === Screen.ScratchpadPage) {
            screenToRender = <ScratchpadPage />;
            gridSystemVariant = GridSystemVariant.OffsetBySidebarAndCentered;
        } else if (screen === Screen.MarketingPage) {
            screenToRender = <MarketingPage />;
        } else if (screen === Screen.LoggedInHomePage) {
            screenToRender = <LoggedInHomePage />;
            gridSystemVariant = GridSystemVariant.Centered;
        } else if (screen === Screen.ClassHomePage) {
            screenToRender = <ClassHomePage />;
        } else if (screen === Screen.Modals) {
            screenToRender = <Modals />;
        }

        return (
            <div className="demo">
                <DemoScreenContainer
                    gridSystemVariant={gridSystemVariant}
                    fixedHeight={fixedHeight}
                    showGridColumns={showGridColumns}
                    showGridRows={showGridRows}
                >
                    {screenToRender}
                </DemoScreenContainer>
                <DemoBar
                    screens={screens}
                    selectedScreen={screen}
                    screenChangeHandler={this.handleScreenChange}
                    dockedSidebar={dockedSidebar}
                    dockedSidebarChangeHandler={this.handleDockedSidebarChange}
                    showGridColumns={showGridColumns}
                    showGridColumnsChangeHandler={
                        this.handleShowGridColumnsChange}
                    showGridRows={showGridRows}
                    showGridRowsChangeHandler={this.handleShowGridRowsChange}
                />
            </div>
        );
    }
    handleUpdateScreenQueryInUrl = (newScreenName) => {
        const newUrl = `${ScreenQueryString}${newScreenName}`;
        // We're using window.history.pushState instead of
        // window.location.href to avoid causing a page reload.
        window.history.pushState("", newScreenName, newUrl);
    }
    handleScreenChange = (screen) => {
        let state = this.state;
        state = update(state, {
            screen: {$set: screen},
        });
        this.setState(state);

        const screensFiltered = Object.keys(Screen).filter(
            (curScreenName) =>
                Screen[curScreenName].toString() === screen.toString());
        if (screensFiltered.length) {
            this.handleUpdateScreenQueryInUrl(screensFiltered[0]);
        }
    }
    handleKeyDown = (event) => {
        if (event.ctrlKey && event.key === "[") {
            this.handlePressLeftArrowKey();
        }
        if (event.ctrlKey && event.key === "]") {
            this.handlePressRightArrowKey();
        }
        if (event.ctrlKey && event.key === "s") {
            this.handleDockedSidebarChange(!this.state.dockedSidebar);
        }
        if (event.ctrlKey && event.key === "c") {
            this.handleShowGridColumnsChange(!this.state.showGridColumns);
        }
        if (event.ctrlKey && event.key === "r") {
            this.handleShowGridRowsChange(!this.state.showGridRows);
        }
    }
    handlePressLeftArrowKey = () => {
        const numScreens = Object.keys(Screen).length;
        let state = this.state;
        let screen = state.screen;
        screen--;
        if (screen < 0) {
            screen = numScreens - 1;
        }
        this.handleScreenChange(screen);
    }
    handlePressRightArrowKey = () => {
        const numScreens = Object.keys(Screen).length;
        let state = this.state;
        let screen = state.screen;
        screen++;
        if (screen >= numScreens) {
            screen = 0;
        }
        this.handleScreenChange(screen);
    }
    handleDockedSidebarChange = (docked) => {
        let state = this.state;
        state = update(state, {
            dockedSidebar: {$set: docked},
        });
        this.setState(state);

        const newSidebarBehavior = docked
            ? FormFactorChangeListener.SidebarBehavior.Docked
            : FormFactorChangeListener.SidebarBehavior.InGrid;
        sharedFormFactorChangeListener.setSidebarBehavior(newSidebarBehavior);
        this.forceUpdate();
    }
    handleShowGridColumnsChange = (show) => {
        let state = this.state;
        state = update(state, {
            showGridColumns: {$set: show},
        });
        this.setState(state);
    }
    handleShowGridRowsChange = (show) => {
        let state = this.state;
        state = update(state, {
            showGridRows: {$set: show},
        });
        this.setState(state);
    }
}

class DemoBar extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            demoBar: {
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                zIndex: 100,
            },
        });

        const {
            screens,
            selectedScreen,
            screenChangeHandler,
            dockedSidebar,
            dockedSidebarChangeHandler,
            showGridColumns,
            showGridColumnsChangeHandler,
            showGridRows,
            showGridRowsChangeHandler,
        } = this.props;

        return (
            <ToolBar styles={[styles.demoBar]}>
                <TempSelectBox
                    items={screens}
                    selectedIndex={selectedScreen}
                    changeHandler={screenChangeHandler}
                />
                <FixedSpace />
                <FlexibleSpace />
                <CheckBox
                    label="Docked sidebar"
                    selected={dockedSidebar}
                    changeHandler={dockedSidebarChangeHandler}
                />
                <FixedSpace />
                <CheckBox
                    label="Grid columns"
                    selected={showGridColumns}
                    changeHandler={showGridColumnsChangeHandler}
                />
                <FixedSpace />
                <CheckBox
                    label="Grid rows"
                    selected={showGridRows}
                    changeHandler={showGridRowsChangeHandler}
                />
            </ToolBar>
        );
    }
}

class DemoScreenContainer extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            screenContainer: {
                position: "absolute",
                top: CoreMetrics.ElementHeight.Large(),
                width: "100%",
            },
            fixedHeightScreenContainer: {
                position: "fixed",
                top: CoreMetrics.ElementHeight.Large(),
                bottom: 0,
                left: 0,
                right: 0,
                overflow: "scroll",
            },
        });

        const {
            children,
            gridSystemVariant,
            fixedHeight,
            showGridColumns,
            showGridRows,
        } = this.props;

        const gridColumns = (showGridColumns)
            ? <GridColumns variant={gridSystemVariant} />
            : null;
        const gridRows = (showGridRows) ? <GridRows /> : null;

        let classes = [styles.screenContainer];
        if (fixedHeight) {
            classes.push(styles.fixedHeightScreenContainer);
        }
        return (
            <div className={css(classes)}>
                {gridRows}
                {gridColumns}
                {children}
            </div>
        );
    }
}

export class GridColumns extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            columnGrid: {
                pointerEvents: "none",
                position: "fixed",
                zIndex: "100",
            },
            expand: {
                width: "100%",
                height: "100%",
            },
            grid: {
                margin: 0,
                marginLeft: "auto",
                marginRight: "auto",
                height: "100%",
            },
            row: {
                height: "100%",
            },
            marker: {
                width: "100%",
                height: "100%",
                backgroundColor: CoreMetrics.Color.DebugFaint,
                textAlign: "center",
                verticalAlign: "middle",
            },
            sidebarMarker: {
                marginLeft: CoreMetrics.Grid.HorizontalMargin,
                marginRight: CoreMetrics.Grid.HorizontalMargin,
                height: "100%",
                backgroundColor: CoreMetrics.Color.DebugFaint,
            },
            phone: {
                [CoreMetrics.MediaQuery().Phone]: {
                    display: "flex",
                },
                [CoreMetrics.MediaQuery().Tablet]: {
                    display: "none",
                },
                [CoreMetrics.MediaQuery().Desktop]: {
                    display: "none",
                },
            },
            tablet: {
                [CoreMetrics.MediaQuery().Phone]: {
                    display: "none",
                },
                [CoreMetrics.MediaQuery().Tablet]: {
                    display: "flex",
                },
                [CoreMetrics.MediaQuery().Desktop]: {
                    display: "none",
                },
            },
            desktop: {
                [CoreMetrics.MediaQuery().Phone]: {
                    display: "none",
                },
                [CoreMetrics.MediaQuery().Tablet]: {
                    display: "none",
                },
                [CoreMetrics.MediaQuery().Desktop]: {
                    display: "flex",
                },
            },
        });
        const marker = <div className={css(styles.marker)}></div>;

        const generateGrid = function(variant, numCols, gridStyles) {
            return (
                <Grid variant={variant} styles={gridStyles}>
                    <GridRow styles={[styles.row]}>
                        {[...Array(numCols).keys()].map((item, i) => {
                            return <GridColumn key={i}>{marker}</GridColumn>;
                        })}
                    </GridRow>
                </Grid>
            );
        };

        const gridSystemVariant = this.props.variant;
        const isCentered = gridSystemVariant === GridSystemVariant.Centered ||
            gridSystemVariant === GridSystemVariant.OffsetBySidebarAndCentered;
        const gridVariant = isCentered
            ? Grid.Variant.Centered
            : Grid.Variant.Stretched;
        const sidebarBehavior = sharedFormFactorChangeListener
            .getSidebarBehavior();

        const phoneGrid = generateGrid(gridVariant,
            CoreMetrics.Grid.ColumnCount().Phone,
            [styles.grid, styles.phone]);
        const tabletGrid = generateGrid(
            gridVariant,
            CoreMetrics.Grid.ColumnCount().Tablet,
            [styles.grid, styles.tablet]);
        const desktopGrid = generateGrid(
            gridVariant,
            CoreMetrics.Grid.ColumnCount().Desktop,
            [styles.grid, styles.desktop]);

        let gridSystem = null;
        if ((sidebarBehavior === FormFactorChangeListener.SidebarBehavior.Docked &&
            gridSystemVariant === GridSystemVariant.OffsetBySidebarAndCentered) ||
            gridSystemVariant === GridSystemVariant.OffsetBySidebarAndStretched
        ) {
            const sidebarMarker = <div className={css(styles.sidebarMarker)} />;
            const content = (
                <div className={css(styles.expand)}>
                    {phoneGrid}
                    {tabletGrid}
                    {desktopGrid}
                </div>
            );
            gridSystem = <DockedTOCandContentLayout
                styles={[styles.columnGrid, styles.expand]}
                toc={sidebarMarker}
                content={content}
            />;
        } else {
            gridSystem = (
                <div className={css(styles.columnGrid, styles.expand)}>
                    {phoneGrid}
                    {tabletGrid}
                    {desktopGrid}
                </div>
            );
        }

        return gridSystem;
    }
}

class GridRows extends React.Component {
    render() {
        const baselineGridGradient = [
            "linear-gradient(0deg",
            CoreMetrics.Color.None + " 0%",
            CoreMetrics.Color.None + " 49%",
            CoreMetrics.Color.DebugFaint + " 50%",
            CoreMetrics.Color.DebugFaint + " 100%",
            "transparent)",
        ].join(", ");

        const styles = createExpandableMediaQueryStylesheet({
            baselineGrid: {
                pointerEvents: "none",
                position: "absolute",
                width: "100%",
                height: "100%",
                zIndex: 100,
                backgroundPosition: "top-left",
                backgroundSize: "8px 8px", // 4-dp baseline grid
                backgroundImage: baselineGridGradient,
            },
        });
        return (
            <div className={css(styles.baselineGrid)}></div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/demo/Demo.jsx